import {
  AgileEngineRevenue,
  ClientGrowth,
} from '../../components/revenueStatusChip/RevenueStatusChip';
import {
  QuarterlyRevenueAccount,
  QuarterlyRevenueAccountSummary,
} from '../../types/quarterlyRevenue';
import {
  SortAccountsBy,
  SortQuarterlyRevenueBy,
} from './quarterlyRevenue.types';

export const agileEngineRevenueOrder = [
  AgileEngineRevenue.GROWING,
  AgileEngineRevenue.FLAT,
  AgileEngineRevenue.DECREASING,
  AgileEngineRevenue.ENDED,
  AgileEngineRevenue.CLOSED,
];

export const clientGrowthOrder = [
  ClientGrowth.GROWING,
  ClientGrowth.FLAT,
  ClientGrowth.DECREASING,
  ClientGrowth.NO_INFO,
];
export const sortAccountsMap: Record<
  SortAccountsBy,
  (a: QuarterlyRevenueAccount, b: QuarterlyRevenueAccount) => number
> = {
  [SortAccountsBy.AccountName]: (a, b) =>
    a.accountName.localeCompare(b.accountName),

  [SortAccountsBy.RevenueStatus]: (a, b) => {
    const aStatus = a.statuses?.[0]?.agileEngineRevenue;
    const bStatus = b.statuses?.[0]?.agileEngineRevenue;
    const aIndex = agileEngineRevenueOrder.indexOf(
      aStatus as AgileEngineRevenue,
    );
    const bIndex = agileEngineRevenueOrder.indexOf(
      bStatus as AgileEngineRevenue,
    );
    return (
      (aIndex === -1 ? agileEngineRevenueOrder.length : aIndex) -
        (bIndex === -1 ? agileEngineRevenueOrder.length : bIndex) ||
      a.accountName.localeCompare(b.accountName)
    );
  },

  [SortAccountsBy.ClientGrowth]: (a, b) => {
    const aStatus = a.statuses?.[0]?.clientGrowth;
    const bStatus = b.statuses?.[0]?.clientGrowth;
    const aIndex = clientGrowthOrder.indexOf(aStatus as ClientGrowth);
    const bIndex = clientGrowthOrder.indexOf(bStatus as ClientGrowth);
    return (
      (aIndex === -1 ? agileEngineRevenueOrder.length : aIndex) -
        (bIndex === -1 ? agileEngineRevenueOrder.length : bIndex) ||
      a.accountName.localeCompare(b.accountName)
    );
  },

  [SortAccountsBy.Headcount]: (a, b) =>
    a.statuses[0].hcDelta - b.statuses[0].hcDelta ||
    a.accountName.localeCompare(b.accountName),

  [SortAccountsBy.AeRevenueDecreaseReasons]: (a, b) => {
    const aReasons = a.statuses?.[0]?.aeRevenueDecreaseReasons ?? [];
    const bReasons = b.statuses?.[0]?.aeRevenueDecreaseReasons ?? [];

    const aVal =
      Array.isArray(aReasons) && aReasons.length > 0 ? aReasons[0] : '';
    const bVal =
      Array.isArray(bReasons) && bReasons.length > 0 ? bReasons[0] : '';

    return (
      String(aVal).localeCompare(String(bVal)) ||
      a.accountName.localeCompare(b.accountName)
    );
  },

  [SortAccountsBy.PDM]: (a, b) =>
    (a.pdmFullName ?? '').localeCompare(b.pdmFullName ?? '') ||
    a.accountName.localeCompare(b.accountName),

  [SortAccountsBy.DM]: (a, b) =>
    (a.dmFullName ?? '').localeCompare(b.dmFullName ?? '') ||
    a.accountName.localeCompare(b.accountName),

  // Merged "PDM / DM" column (narrower screens only): nested sort, primary
  // by PDM, tie-broken by DM, then by account name.
  [SortAccountsBy.PDMAndDM]: (a, b) =>
    (a.pdmFullName ?? '').localeCompare(b.pdmFullName ?? '') ||
    (a.dmFullName ?? '').localeCompare(b.dmFullName ?? '') ||
    a.accountName.localeCompare(b.accountName),
};

export const sortQuarterlyRevenueMap: Record<
  SortQuarterlyRevenueBy,
  (
    a: QuarterlyRevenueAccountSummary,
    b: QuarterlyRevenueAccountSummary,
  ) => number
> = {
  [SortQuarterlyRevenueBy.Quarter]: (a, b) => a.quarter - b.quarter,
  [SortQuarterlyRevenueBy.AeRevenueDecreaseReasons]: (a, b) => {
    const aReasons = a?.aeRevenueDecreaseReasons ?? [];
    const bReasons = b?.aeRevenueDecreaseReasons ?? [];

    const aVal =
      Array.isArray(aReasons) && aReasons.length > 0 ? aReasons[0] : '';
    const bVal =
      Array.isArray(bReasons) && bReasons.length > 0 ? bReasons[0] : '';

    return String(aVal).localeCompare(String(bVal));
  },
  [SortQuarterlyRevenueBy.Headcount]: (a, b) => a.hcDelta - b.hcDelta,
  [SortQuarterlyRevenueBy.RevenueStatus]: (a, b) => {
    const aStatus = a.agileEngineRevenue;
    const bStatus = b.agileEngineRevenue;
    const aIndex = agileEngineRevenueOrder.indexOf(
      aStatus as AgileEngineRevenue,
    );
    const bIndex = agileEngineRevenueOrder.indexOf(
      bStatus as AgileEngineRevenue,
    );
    return (
      (aIndex === -1 ? agileEngineRevenueOrder.length : aIndex) -
      (bIndex === -1 ? agileEngineRevenueOrder.length : bIndex)
    );
  },

  [SortQuarterlyRevenueBy.ClientGrowth]: (a, b) => {
    const aStatus = a.clientGrowth;
    const bStatus = b.clientGrowth;
    const aIndex = clientGrowthOrder.indexOf(aStatus as ClientGrowth);
    const bIndex = clientGrowthOrder.indexOf(bStatus as ClientGrowth);
    return (
      (aIndex === -1 ? agileEngineRevenueOrder.length : aIndex) -
      (bIndex === -1 ? agileEngineRevenueOrder.length : bIndex)
    );
  },
};
